import { MultiSelect } from 'primereact/multiselect'
import { AgCharts } from 'ag-charts-react'
import { FC, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { SelectOption } from '../../types/types'
import { themeState } from '../../utils/state-atoms'

const GROUP_MONITORS: SelectOption[] = [
  {
    label: 'dCD',
    value: 'delta_cd'
  },
  {
    label: 'dCL',
    value: 'delta_cl'
  },
  {
    label: 'dCS',
    value: 'delta_cs'
  },
  {
    label: 'CD',
    value: 'cd'
  },
  {
    label: 'CL',
    value: 'cl'
  },
  {
    label: 'CS',
    value: 'cs'
  }
]

const ABSOLUTE_BAR_FILL = '#2563eb'

// Used only when several monitors share a chart, so each series is distinguishable.
const SERIES_FILLS = ['#2563eb', '#f59e0b', '#7c3aed', '#0d9488', '#db2777', '#65a30d']

const isDeltaMonitor = (field?: string) => !!field && field.startsWith('delta_')

export type GroupForceChartConfig = { key: number; fields: string[] }

type GroupForceChartPanelProps = {
  groupForceData: Record<string, any>[] | null
  rowIndex: number
  colIndex: number
  charts: GroupForceChartConfig[][]
  onAddRight?: (rowIndex: number, colIndex: number) => void
  onAddUnder?: (rowIndex: number) => void
  onRemove?: (rowIndex: number, colIndex: number) => void
  onMonitorChange?: (rowIndex: number, colIndex: number, monitors: string[]) => void
}

const GroupForceChartPanel: FC<GroupForceChartPanelProps> = ({
  groupForceData,
  rowIndex,
  colIndex,
  charts,
  onAddRight,
  onAddUnder,
  onRemove,
  onMonitorChange
}) => {
  // MultiSelect resolves each option down to its `value`, so selection state is field strings.
  const [selectedFields, setSelectedFields] = useState<string[]>(() => {
    const fields = charts[rowIndex][colIndex].fields || []
    return GROUP_MONITORS.filter(option => fields.includes(option.value)).map(
      option => option.value
    )
  })
  const appTheme = useRecoilValue(themeState)

  const selectedMonitors = useMemo(
    () => GROUP_MONITORS.filter(option => selectedFields.includes(option.value)),
    [selectedFields]
  )

  const chartOptions = useMemo(() => {
    // Mirror the table's delta logic: when a Ref 2 value exists, compute the
    // delta against it instead of using the pre-computed delta field.
    const computeValue = (item: Record<string, any>, field: string) => {
      if (field === 'delta_cd' && item['ref2_cd'] != null && item['cd'] != null) {
        return Number(item['cd']) - Number(item['ref2_cd'])
      }
      if (field === 'delta_cl' && item['ref2_cl'] != null && item['cl'] != null) {
        return Number(item['cl']) - Number(item['ref2_cl'])
      }
      if (field === 'delta_cs' && item['ref2_cs'] != null && item['cs'] != null) {
        return Number(item['cs']) - Number(item['ref2_cs'])
      }
      return Number(item[field])
    }

    const data = (groupForceData || []).map(item => {
      const row: Record<string, any> = { group: item.group }
      selectedMonitors.forEach(monitor => {
        row[monitor.value] = computeValue(item, monitor.value)
      })
      return row
    })

    const isMulti = selectedMonitors.length > 1
    const values = data.flatMap(row =>
      selectedMonitors.map(monitor => Number(row[monitor.value])).filter(Number.isFinite)
    )
    const maxAbs = Math.max(0, ...values.map(value => Math.abs(value)))
    // vertical labels grow along the axis with their character count
    const maxLabelChars = Math.max(1, ...values.map(value => value.toFixed(4).length))
    const AXIS_PADDING = (maxAbs || 0.001) * 0.12 * (isMulti ? 1 : maxLabelChars)
    const yMax = Math.max(0, ...values) + AXIS_PADDING
    const yMin = Math.min(0, ...values) - AXIS_PADDING

    return {
      data,
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default',
      background: { fill: 'transparent' },
      series: selectedMonitors.map((monitor, index) => ({
        type: 'bar',
        xKey: 'group',
        yKey: monitor.value,
        yName: monitor.label,
        // Multiple bar series stack by default; stacking unrelated coefficients is meaningless.
        grouped: true,
        cornerRadius: 4,
        // Bar labels only stay legible on a single series.
        label: {
          enabled: !isMulti,
          orientation: 'vertical',
          placement: 'outside-end',
          formatter: ({ value }: { value: number }) => value.toFixed(4)
        },
        tooltip: {
          range: 'nearest',
          renderer: ({ datum }: { datum: Record<string, any> }) => ({
            title: datum.group,
            content: `${monitor.label}: ${Number(datum[monitor.value]).toFixed(4)}`
          })
        },
        ...(isMulti
          ? { fill: SERIES_FILLS[index % SERIES_FILLS.length] }
          : {
              itemStyler: ({ datum }: { datum: Record<string, any> }) => ({
                fill: isDeltaMonitor(monitor.value)
                  ? Number(datum[monitor.value]) > 0
                    ? '#dc2626'
                    : '#16a34a'
                  : ABSOLUTE_BAR_FILL
              })
            })
      })),
      axes: {
        x: {
          type: 'category',
          position: 'bottom',
          label: {
            autoRotate: false,
            avoidCollisions: false,
            wrapping: 'never',
            truncate: false,
            rotation: -45
          }
        },
        y: { type: 'number', position: 'left', min: yMin, max: yMax }
      },
      legend: { enabled: isMulti, position: 'top' }
    }
  }, [selectedMonitors, groupForceData, appTheme])

  return (
    <div className="flex flex-col basis-0 grow items-stretch space-y-2 rounded-md p-4 border border-gray-300 dark:border-neutral-700">
      <div className="flex flex-row items-start justify-between basis-[60px] border-b border-gray-300 dark:border-neutral-700">
        <div className="flex items-center">
          <MultiSelect
            value={selectedFields}
            options={GROUP_MONITORS}
            optionLabel="label"
            optionValue="value"
            onChange={event => {
              const fields = (event.value || []) as string[]
              setSelectedFields(fields)
              onMonitorChange?.(rowIndex, colIndex, fields)
            }}
            placeholder="Select"
            maxSelectedLabels={3}
            selectedItemsLabel="{0} monitors"
            showSelectAll={false}
            className="w-52"
            pt={{
              labelContainer: {
                className: '!py-2'
              }
            }}
          />
        </div>
        <div className="flex items-center justify-end space-x-2 h-12">
          {selectedMonitors.length === 1 && isDeltaMonitor(selectedMonitors[0].value) && (
            <>
              <div className="h-2 w-2 bg-red-500 rounded-[4px]"></div>
              <div>Positive</div>
              <div className="w-2"></div>
              <div className="h-2 w-2 bg-green-500 rounded-[4px]"></div>
              <div>Negative</div>
            </>
          )}
          <div className="flex items-center space-x-2 pl-2">
            {charts[rowIndex].length < 3 && colIndex < 2 && (
              <button
                aria-label="Add chart to the right"
                className="flex items-center gap-1 px-[6px] py-[3px] rounded text-[12px] bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#45556c] hover:text-[#08498a] transition-colors border border-[#ddd] dark:bg-neutral-70 dark:text-neutral-10 dark:border-neutral-20 dark:hover:bg-neutral-60"
                onClick={() => onAddRight?.(rowIndex, colIndex)}>
                <i className="pi pi-plus text-[0.6rem] mt-[2px]"></i>
                <i className="pi pi-arrow-right text-[0.6rem]  mt-[2px]"></i>
              </button>
            )}
            {charts.length < 3 && rowIndex < 2 && (
              <button
                aria-label="Add chart below"
                className="flex items-center gap-1 px-[6px] py-[3px] rounded text-[12px] bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#45556c] hover:text-[#08498a] transition-colors border border-[#ddd] dark:bg-neutral-70 dark:text-neutral-10 dark:border-neutral-20 dark:hover:bg-neutral-60"
                onClick={() => onAddUnder?.(rowIndex)}>
                <i className="pi pi-plus text-[0.6rem] mt-[2px]"></i>
                <i className="pi pi-arrow-down text-[0.6rem] mt-[2px]"></i>
              </button>
            )}
            {(charts.length > 1 || (charts.length === 1 && charts[0].length > 1)) && (
              <button
                aria-label="Remove chart"
                className="text-lg flex items-center justify-center text-neutral-70 dark:text-white"
                onClick={() => onRemove?.(rowIndex, colIndex)}>
                <i className="pi pi-times text-[0.75rem] mt-[2px]"></i>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="grow min-h-0">
        {selectedMonitors.length > 0 ? (
          <div className="h-full w-full">
            <AgCharts options={chartOptions as any} style={{ height: '100%', width: '100%' }} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-neutral-400">
            Select a monitor to view its chart.
          </div>
        )}
      </div>
    </div>
  )
}

export default GroupForceChartPanel
